import type { PaymentStatus } from "@/types/api";

/**
 * Payment utility functions and constants
 */

export const PAYMENT_STATUS = {
  PENDING: "pending",
  COMPLETED: "completed",
  FAILED: "failed",
  REFUNDED: "refunded",
  CANCELLED: "cancelled",
} as const;

export interface PaymentStatusStyle {
  badge: string;
  icon: string;
}

/**
 * Get Tailwind CSS classes for payment status badge
 */
export function getPaymentStatusStyle(status: PaymentStatus): PaymentStatusStyle {
  const styles: Record<PaymentStatus, PaymentStatusStyle> = {
    completed: {
      badge: "bg-green-50 text-green-700 border-green-200",
      icon: "text-green-600",
    },
    pending: {
      badge: "bg-amber-50 text-amber-700 border-amber-200",
      icon: "text-amber-600",
    },
    failed: {
      badge: "bg-red-50 text-red-700 border-red-200",
      icon: "text-red-600",
    },
    cancelled: {
      badge: "bg-red-50 text-red-700 border-red-200",
      icon: "text-red-600",
    },
    refunded: {
      badge: "bg-gray-50 text-gray-700 border-gray-200",
      icon: "text-gray-600",
    },
  };

  return styles[status] || styles.pending;
}

/**
 * Get admin badge style for payment status
 */
export function getAdminPaymentStatusStyle(status: string): string {
  const styles: Record<string, string> = {
    completed: "bg-green-100 text-green-800",
    pending: "bg-yellow-100 text-yellow-800",
    failed: "bg-red-100 text-red-800",
    cancelled: "bg-red-100 text-red-800",
    refunded: "bg-gray-100 text-gray-800",
  };

  return styles[status] || "bg-gray-100 text-gray-800";
}

/**
 * Format currency amount from cents to display string
 */
export function formatCurrency(amountInCents: number, currency = "gbp"): string {
  const symbols: Record<string, string> = { usd: "$", gbp: "£", eur: "€" };
  const symbol = symbols[currency.toLowerCase()] ?? currency.toUpperCase();
  const amount = (amountInCents / 100).toFixed(2);
  return `${symbol}${amount}`;
}

/**
 * Check if payment can be resumed
 */
export function canResumePayment(status: PaymentStatus): boolean {
  return status === PAYMENT_STATUS.PENDING;
}

/**
 * Check if payment has a receipt available.
 *
 * Stripe issues no receipt for a fully-discounted (£0) checkout — there's no
 * PaymentIntent — so hide the button when nothing was charged. `amountTotal`
 * is null for rows not yet reconciled from Stripe; treat those as chargeable.
 */
export function hasReceipt(status: PaymentStatus, amountTotal: number | null): boolean {
  if (status !== PAYMENT_STATUS.COMPLETED) return false;
  return amountTotal === null || amountTotal > 0;
}
